"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { ArrowRight } from "lucide-react";
import { PhotoCarousel } from "@/components/events/PhotoCarousel";
import { formats, type EventFormat } from "@/components/events/eventsData";

/*
 * "Five formats, one community." From 1024px the format list sticks on the left
 * while the panels scroll on the right: the row whose panel crosses the middle
 * of the screen turns ink, the others stay at 40%, and the rule fills one fifth
 * per format. Clicking a row scrolls to its panel. Below 1024px the list is
 * hidden and the panels stack.
 */

const HEADER_OFFSET = 120;

const external = (href: string) => /^https?:/.test(href);

/** YouTube-style play button that appears over the podcast photo on hover and focus. */
const PlayOverlay: React.FC = () => (
  <span
    aria-hidden="true"
    className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors duration-300 group-hover:bg-black/20 group-focus-visible:bg-black/20"
  >
    <span className="flex h-14 w-20 scale-90 items-center justify-center rounded-2xl bg-[#FF0000] opacity-0 shadow-[0_10px_30px_rgba(0,0,0,0.35)] transition-[opacity,transform] duration-300 ease-out group-hover:scale-100 group-hover:opacity-100 group-focus-visible:scale-100 group-focus-visible:opacity-100 motion-reduce:transition-none">
      <svg viewBox="0 0 24 24" className="ml-1 h-7 w-7 fill-white">
        <path d="M8 5.5v13l10.5-6.5z" />
      </svg>
    </span>
  </span>
);

const Panel = React.forwardRef<
  HTMLElement,
  { format: EventFormat; index: number }
>(({ format, index }, ref) => {
  const { media } = format;
  return (
    <article ref={ref} id={`format-${format.id}`} className="scroll-mt-32">
      {format.gallery ? (
        <PhotoCarousel
          photos={format.gallery}
          label={`${format.title} photos`}
        />
      ) : (
        <>
          {/* The photo opens the format's own page (the podcast opens the channel). */}
          <a
            href={format.link.href}
            {...(external(format.link.href)
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
            aria-label={`${format.link.label}: ${format.title}`}
            className="group block rounded-[24px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#F97316]"
          >
            {/* Panels scale by the design's 648px-wide ratios. */}
            {media.kind === "photo" ? (
              <div
                className="relative overflow-hidden rounded-[24px]"
                style={{ aspectRatio: `648 / ${media.height}` }}
              >
                <Image
                  src={media.src}
                  alt={media.alt}
                  fill
                  sizes="(min-width: 1024px) 648px, calc(100vw - 40px)"
                  quality={80}
                  className="object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03] motion-reduce:transition-none"
                />
                {format.id === "podcast" && <PlayOverlay />}
              </div>
            ) : (
              <div className="flex aspect-[648/432] items-center justify-center overflow-hidden rounded-[24px] bg-wcd-faq">
                <Image
                  src={media.src}
                  alt={media.alt}
                  width={media.width}
                  height={media.height}
                  sizes="(min-width: 1024px) 370px, 60vw"
                  className="h-auto rounded-[12px] shadow-wcd-card-sm transition-transform duration-500 ease-out group-hover:scale-[1.03] motion-reduce:transition-none"
                  style={{ width: `${(media.width / 648) * 100}%` }}
                />
              </div>
            )}
          </a>
        </>
      )}
      <div className="flex flex-col gap-3.5 pt-6 md:pt-8">
        <p className="text-xs text-wcd-caption">
          {String(index + 1).padStart(2, "0")} /{" "}
          {String(formats.length).padStart(2, "0")}
        </p>
        <h3 className="font-prociono text-3xl leading-tight text-wcd-heading md:text-4xl md:leading-10">
          {format.title}
        </h3>
        <p className="max-w-[540px] text-base leading-6 text-wcd-body">
          {format.description}
        </p>
        <p className="flex items-center gap-3 pt-1.5 text-sm font-medium text-wcd-heading">
          <span
            aria-hidden="true"
            className="hidden h-px w-6 shrink-0 bg-wcd-ink md:block"
          />
          {format.proof}
        </p>
        <a
          href={format.link.href}
          {...(external(format.link.href)
            ? { target: "_blank", rel: "noopener noreferrer" }
            : {})}
          className="inline-flex w-fit items-center gap-1.5 pt-1.5 text-sm font-medium text-wcd-ink transition hover:text-wcd-accent-deep"
        >
          {format.link.label}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </a>
      </div>
    </article>
  );
});
Panel.displayName = "Panel";

export const FormatIndex: React.FC = () => {
  const [active, setActive] = useState(0);
  const panels = useRef<Array<HTMLElement | null>>([]);

  useEffect(() => {
    if (!("IntersectionObserver" in window)) return;
    // A one-pixel band across the middle of the viewport decides the active panel.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting)
            setActive(panels.current.indexOf(entry.target as HTMLElement));
        });
      },
      { rootMargin: "-50% 0px -50% 0px" },
    );
    panels.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const goTo = (i: number) => {
    const el = panels.current[i];
    if (!el) return;
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    window.scrollTo({
      top: el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET,
      behavior: reduce ? "auto" : "smooth",
    });
  };

  return (
    <div className="grid gap-16 lg:grid-cols-[360px_minmax(0,1fr)] lg:gap-24">
      <nav
        aria-label="Event formats"
        className="sticky top-[120px] hidden self-start lg:flex lg:gap-7"
      >
        <span
          aria-hidden="true"
          className="relative w-[2px] shrink-0 bg-wcd-divider"
        >
          <span
            className="absolute inset-x-0 top-0 bg-wcd-ink transition-[height] duration-500 ease-out motion-reduce:transition-none"
            style={{ height: `${((active + 1) / formats.length) * 100}%` }}
          />
        </span>
        <ol className="flex w-[330px] flex-col gap-8">
          {formats.map((f, i) => {
            const current = i === active;
            return (
              <li key={f.id}>
                <button
                  type="button"
                  onClick={() => goTo(i)}
                  aria-current={current ? "true" : undefined}
                  className="group flex w-full gap-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#F97316]"
                >
                  <span
                    className={`text-xs leading-4 transition-colors ${current ? "text-wcd-heading" : "text-wcd-caption opacity-70"}`}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="flex flex-col gap-1">
                    <span
                      className={`font-prociono text-[30px] leading-9 text-wcd-heading transition-opacity duration-300 ${
                        current
                          ? "opacity-100"
                          : "opacity-40 group-hover:opacity-70"
                      }`}
                    >
                      {f.name}
                    </span>
                    <span
                      className={`text-xs leading-4 transition-colors ${current ? "text-wcd-body" : "text-wcd-caption opacity-70"}`}
                    >
                      {f.indexLine}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>

      <div className="flex flex-col gap-20 lg:gap-[120px]">
        {formats.map((f, i) => (
          <Panel
            key={f.id}
            format={f}
            index={i}
            ref={(el) => {
              panels.current[i] = el;
            }}
          />
        ))}
      </div>
    </div>
  );
};
